import React, { useState, useEffect } from 'react';
import {
    Building2, PlusCircle, Trash2, LogOut, Search,
    Pencil, Loader2, Users, FileText, Mail, Calendar,
    PlayCircle, Filter, DollarSign, Ban, CheckCircle,
    BarChart3, Send, Image as ImageIcon, Wifi, WifiOff, AlertTriangle, Clock, HelpCircle, Key,
    Headphones, Phone, MessageSquare, ExternalLink, Save, Check
} from 'lucide-react';
import api from '../services/api'; // Axios Local
import Modal from '../components/Modal';

const SupportPanel = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supportConfig, setSupportConfig] = useState({
        supportWhatsapp: '',
        supportEmail: '',
        supportMessage: '',
        supportHours: ''
    });
    const [savingConfig, setSavingConfig] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [ticketsRes, settingsRes] = await Promise.all([
                api.get('/admin/support'),
                api.get('/system/settings')
            ]);
            setTickets(ticketsRes.data || []);
            if (settingsRes.data) {
                setSupportConfig({
                    supportWhatsapp: settingsRes.data.supportWhatsapp || '',
                    supportEmail: settingsRes.data.supportEmail || '',
                    supportMessage: settingsRes.data.supportMessage || 'Olá! Preciso de suporte com a plataforma WaDesk.',
                    supportHours: settingsRes.data.supportHours || 'Segunda a Sexta, das 08h às 18h'
                });
            }
        } catch (e) {
            console.error("Erro ao carregar dados de suporte", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const handleSaveSupportConfig = async (e) => {
        e.preventDefault();
        setSavingConfig(true);
        setSaveSuccess(false);
        try {
            await api.put('/admin/settings', supportConfig);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3500);
        } catch (e) {
            alert("Erro ao salvar canais de suporte: " + (e.response?.data?.error || e.message));
        } finally {
            setSavingConfig(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.put(`/admin/support/${id}/status`, { status });
            loadData();
        } catch (e) {
            alert("Erro ao atualizar status");
        }
    };

    if (loading) return <div className="text-center py-20"><Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600" /></div>;

    const rawPhone = supportConfig.supportWhatsapp ? supportConfig.supportWhatsapp.replace(/\D/g, '') : '';
    const testWaUrl = rawPhone ? `https://wa.me/${rawPhone}?text=${encodeURIComponent(supportConfig.supportMessage || 'Teste de suporte')}` : null;

    return (
        <div className="space-y-6">
            {/* CARD 1: CONFIGURAÇÃO DOS CANAIS DE SUPORTE */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between border-b pb-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center">
                            <Headphones className="w-6 h-6 mr-2 text-blue-600" />
                            Canais de Atendimento ao Cliente (WhatsApp e E-mail)
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Configure o número de WhatsApp e o e-mail oficial que serão exibidos no menu principal de todas as empresas e usuários para solicitação de suporte.
                        </p>
                    </div>
                    {saveSuccess && (
                        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 animate-in fade-in">
                            <Check className="w-4 h-4" /> Configurações salvas com sucesso!
                        </div>
                    )}
                </div>

                <form onSubmit={handleSaveSupportConfig} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* WhatsApp */}
                        <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-4 space-y-2">
                            <label className="block text-xs font-bold text-emerald-900 uppercase flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <MessageSquare className="w-4 h-4 text-emerald-600" />
                                    WhatsApp Oficial de Suporte
                                </span>
                                {testWaUrl && (
                                    <a
                                        href={testWaUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[11px] font-semibold text-emerald-700 hover:underline flex items-center gap-1"
                                    >
                                        Testar WhatsApp <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                            </label>
                            <input
                                type="text"
                                className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                                placeholder="Ex: 5521999999999 (com DDI e DDD)"
                                value={supportConfig.supportWhatsapp}
                                onChange={e => setSupportConfig({ ...supportConfig, supportWhatsapp: e.target.value })}
                            />
                            <p className="text-[11px] text-gray-500">
                                Digite o número com código do país e DDD (Ex: <strong>5521999998888</strong>).
                            </p>
                        </div>

                        {/* E-mail */}
                        <div className="bg-blue-50/40 border border-blue-100 rounded-xl p-4 space-y-2">
                            <label className="block text-xs font-bold text-blue-900 uppercase flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <Mail className="w-4 h-4 text-blue-600" />
                                    E-mail Oficial de Suporte
                                </span>
                                {supportConfig.supportEmail && (
                                    <a
                                        href={`mailto:${supportConfig.supportEmail}`}
                                        className="text-[11px] font-semibold text-blue-700 hover:underline flex items-center gap-1"
                                    >
                                        Testar E-mail <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                            </label>
                            <input
                                type="email"
                                className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                placeholder="suporte@seudominio.com.br"
                                value={supportConfig.supportEmail}
                                onChange={e => setSupportConfig({ ...supportConfig, supportEmail: e.target.value })}
                            />
                            <p className="text-[11px] text-gray-500">
                                Endereço de e-mail que receberá os chamados e dúvidas.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Horário */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1 flex items-center gap-1.5">
                                <Clock className="w-4 h-4 text-gray-500" />
                                Horário de Atendimento Informado
                            </label>
                            <input
                                type="text"
                                className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="Ex: Segunda a Sexta, das 08:00 às 18:00"
                                value={supportConfig.supportHours}
                                onChange={e => setSupportConfig({ ...supportConfig, supportHours: e.target.value })}
                            />
                        </div>

                        {/* Mensagem Padrão */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                Mensagem Inicial Padrão do WhatsApp
                            </label>
                            <input
                                type="text"
                                className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="Texto pré-digitado que abrirá no WhatsApp do cliente"
                                value={supportConfig.supportMessage}
                                onChange={e => setSupportConfig({ ...supportConfig, supportMessage: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="pt-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingConfig}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg text-sm shadow-sm transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Salvar Configurações de Suporte
                        </button>
                    </div>
                </form>
            </div>

            {/* CARD 2: CHAMADOS REGISTRADOS */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between border-b pb-4 mb-4">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <HelpCircle className="w-5 h-5 mr-2 text-blue-600" />
                        Chamados de Suporte Registrados ({tickets.length})
                    </h2>
                    <button
                        onClick={loadData}
                        className="text-xs text-blue-600 hover:underline font-semibold"
                    >
                        Atualizar Lista
                    </button>
                </div>

                {tickets.length === 0 ? (
                    <p className="text-gray-500 py-6 text-center text-sm">Nenhum chamado registrado no momento.</p>
                ) : (
                    <div className="space-y-4">
                        {tickets.map(t => (
                            <div key={t._id} className="border p-4 rounded-xl bg-gray-50 flex flex-col md:flex-row gap-4 hover:border-gray-300 transition-colors">
                                <div className="flex-1">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="font-semibold text-gray-900 text-sm">
                                            Doc: <span className="font-mono text-gray-600">{t.cpf_cnpj}</span> | Tel: <span className="font-mono text-blue-600 font-bold">{t.phone}</span>
                                        </div>
                                        <select
                                            value={t.status}
                                            onChange={e => updateStatus(t._id, e.target.value)}
                                            className={`border p-1 rounded-md text-xs font-bold outline-none ${
                                                t.status === 'resolved'
                                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                                    : t.status === 'in_progress'
                                                    ? 'bg-amber-50 text-amber-800 border-amber-300'
                                                    : 'bg-red-50 text-red-800 border-red-300'
                                            }`}
                                        >
                                            <option value="open">Aberto</option>
                                            <option value="in_progress">Em Andamento</option>
                                            <option value="resolved">Resolvido</option>
                                        </select>
                                    </div>
                                    <p className="text-gray-800 text-sm bg-white p-3 border rounded-lg whitespace-pre-wrap">{t.reason}</p>
                                    <div className="text-xs text-gray-400 mt-2">Criado em: {new Date(t.createdAt).toLocaleString('pt-BR')}</div>
                                </div>
                                {t.images && t.images.length > 0 && (
                                    <div className="flex gap-2 items-center flex-wrap md:w-64 border-t md:border-t-0 md:border-l pt-3 md:pt-0 md:pl-3">
                                        {t.images.map((img, i) => (
                                            <a key={i} href={api.defaults.baseURL + img} target="_blank" rel="noopener noreferrer">
                                                <img src={api.defaults.baseURL + img} alt="Anexo" className="w-14 h-14 object-cover rounded-lg border shadow-sm hover:scale-105 transition-transform" />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const SystemNoticesPanel = () => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [form, setForm] = useState({ title: '', message: '', imageUrl: '', targetCompanyId: 'all' });
    const [companies, setCompanies] = useState([]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [noticesRes, compRes] = await Promise.all([
                api.get('/admin/system-notices'),
                api.get('/admin/companies')
            ]);
            setNotices(noticesRes.data);
            setCompanies(compRes.data);
        } catch (e) {
            console.error("Erro ao carregar avisos", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Deseja excluir este aviso?")) return;
        try {
            await api.delete(`/admin/system-notices/${id}`);
            loadData();
        } catch (e) {
            alert("Erro ao excluir.");
        }
    };

    const handleCreate = async () => {
        if (!form.title || !form.message) return alert("Título e mensagem obrigatórios.");
        try {
            await api.post('/admin/system-notices', form);
            alert("Aviso enviado!");
            setIsCreateOpen(false);
            setForm({ title: '', message: '', imageUrl: '', targetCompanyId: 'all' });
            loadData();
        } catch (e) {
            alert("Erro ao enviar.");
        }
    };

    if (loading) return <div className="text-center py-20"><Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600" /></div>;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold flex items-center"><Mail className="w-5 h-5 mr-2 text-blue-600" /> Avisos do Sistema</h2>
                <button onClick={() => setIsCreateOpen(true)} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center font-medium shadow-sm">
                    <Send className="w-4 h-4 mr-2" /> Novo Aviso
                </button>
            </div>

            <div className="space-y-4">
                {notices.length === 0 ? <p className="text-gray-500">Nenhum aviso encontrado.</p> : notices.map(n => (
                    <div key={n._id} className="border p-4 rounded-lg bg-gray-50 flex justify-between items-start">
                        <div>
                            <h3 className="font-bold text-gray-800">{n.title}</h3>
                            <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{n.message}</p>
                            <div className="text-xs text-gray-400 mt-2 flex gap-4">
                                <span>Criado em: {new Date(n.createdAt).toLocaleString('pt-BR')}</span>
                                <span>Alvo: {n.targetCompanyIds && n.targetCompanyIds.length > 0 ? `${n.targetCompanyIds.length} empresas` : 'Global/Sem empresas'}</span>
                            </div>
                        </div>
                        <button onClick={() => handleDelete(n._id)} className="text-red-500 hover:bg-red-50 p-2 rounded-md">
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                ))}
            </div>

            <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Novo Aviso" confirmText="Enviar" onConfirm={handleCreate}>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Título</label>
                        <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ex: Manutenção Programada" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Mensagem</label>
                        <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-blue-500 h-24" placeholder="Detalhes do aviso..."></textarea>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">URL da Imagem (Opcional)</label>
                        <input type="text" value={form.imageUrl} onChange={e => setForm({...form, imageUrl: e.target.value})} className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="https://..." />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Público Alvo</label>
                        <select value={form.targetCompanyId} onChange={e => setForm({...form, targetCompanyId: e.target.value})} className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                            <option value="all">Todas as Empresas Atuais</option>
                            {companies.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>)}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Se escolher 'Todas', o aviso será enviado para as empresas existentes neste momento.</p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const ModulesPanel = ({ systemSettings, onSaveSettings, companies }) => {
    const [prices, setPrices] = useState({
        modulePriceNfse: systemSettings.modulePriceNfse ?? 49.90,
        modulePriceBoletos: systemSettings.modulePriceBoletos ?? 49.90
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setPrices({
            modulePriceNfse: systemSettings.modulePriceNfse ?? 49.90,
            modulePriceBoletos: systemSettings.modulePriceBoletos ?? 49.90
        });
    }, [systemSettings]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSaveSettings({
                modulePriceNfse: parseFloat(prices.modulePriceNfse) || 0,
                modulePriceBoletos: parseFloat(prices.modulePriceBoletos) || 0
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            alert('Erro ao salvar preços dos módulos: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const countNfse = (companies || []).filter(c => c.modules?.notas_fiscais).length;
    const countBoletos = (companies || []).filter(c => c.modules?.financeiro).length;

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <DollarSign className="w-6 h-6 text-emerald-600" />
                            Valores dos Módulos Adicionais
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Defina os preços mensais cobrados quando os clientes contratam esses módulos adicionais na tela de Assinatura.
                        </p>
                    </div>
                    {saved && (
                        <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg border border-green-200 text-sm font-semibold animate-in fade-in">
                            <Check className="w-4 h-4" /> Valores salvos com sucesso!
                        </div>
                    )}
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Módulo NFS-e */}
                        <div className="p-5 rounded-xl border-2 border-blue-100 bg-blue-50/30 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 font-bold text-lg text-blue-900">
                                        <FileText className="w-5 h-5 text-blue-600" />
                                        Módulo Emissão de NFS-e
                                    </div>
                                    <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full">
                                        {countNfse} empresa(s) ativa(s)
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                    Permite que o cliente emita Notas Fiscais de Serviço eletrônicas com alíquotas municipais, cálculo automático de tributos e integração direta com a prefeitura via Focus NFe.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Valor Mensal Adicional (R$)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">R$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        value={prices.modulePriceNfse}
                                        onChange={e => setPrices({ ...prices, modulePriceNfse: e.target.value })}
                                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-800 bg-white"
                                        placeholder="49.90"
                                    />
                                </div>
                                <p className="text-[11px] text-gray-500 mt-1">Cobrado mensalmente junto à fatura ou via pró-rata na contratação.</p>
                            </div>
                        </div>

                        {/* Módulo Boletos / Financeiro */}
                        <div className="p-5 rounded-xl border-2 border-emerald-100 bg-emerald-50/30 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 font-bold text-lg text-emerald-900">
                                        <DollarSign className="w-5 h-5 text-emerald-600" />
                                        Módulo Emissão de Boletos (Financeiro)
                                    </div>
                                    <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
                                        {countBoletos} empresa(s) ativa(s)
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                    Libera o painel completo de Gestão Financeira com emissão automatizada de boletos bancários (linha digitável, QR Code Pix), fluxo de caixa e envio de 2ª via.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    Valor Mensal Adicional (R$)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">R$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        value={prices.modulePriceBoletos}
                                        onChange={e => setPrices({ ...prices, modulePriceBoletos: e.target.value })}
                                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none font-bold text-gray-800 bg-white"
                                        placeholder="49.90"
                                    />
                                </div>
                                <p className="text-[11px] text-gray-500 mt-1">Cobrado mensalmente junto à fatura ou via pró-rata na contratação.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center font-bold text-sm shadow-sm transition-colors"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                            Salvar Preços dos Módulos
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SuperAdminScreen = ({ onLogout }) => {
    const [activeTab, setActiveTab] = useState('companies');
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // Configurações do Sistema
    const [systemSettings, setSystemSettings] = useState({ allowTrialRegistration: false });
    const [settingsUpdating, setSettingsUpdating] = useState(false);

    // Filtros e Modais
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Formulários
    const [newCompany, setNewCompany] = useState({
        name: '', adminName: '', adminEmail: '', adminPassword: '', cpf_cnpj: '', maxUsers: 3,
        modules: { financeiro: false, notas_fiscais: false, integracoes: false }, next_due_date: ''
    });
    const [editingCompany, setEditingCompany] = useState(null);
    const [selectedDashboardCompany, setSelectedDashboardCompany] = useState(null);
    
    // Notificações
    const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(false);
    const [noticeForm, setNoticeForm] = useState({ title: '', message: '', imageUrl: '', targetCompanyId: '' });

    // 1. CARREGAR EMPRESAS E CONFIGURAÇÕES DA API
    const fetchData = async () => {
        setLoading(true);
        try {
            const [compRes, setRes] = await Promise.all([
                api.get('/admin/companies'),
                api.get('/system/settings')
            ]);
            setCompanies(compRes.data);
            setSystemSettings(setRes.data || { allowTrialRegistration: false });
        } catch (error) {
            console.error("Erro ao carregar dados iniciais:", error);
            alert("Erro de conexão com servidor local.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // 1.1 ATUALIZAR CONFIGURAÇÃO (TRIAL)
    const handleToggleTrial = async () => {
        setSettingsUpdating(true);
        const newValue = !systemSettings.allowTrialRegistration;
        setSystemSettings({ ...systemSettings, allowTrialRegistration: newValue }); // optimistic update
        try {
            await api.put('/admin/settings', { allowTrialRegistration: newValue });
        } catch (error) {
            alert("Erro ao atualizar o Cadastro. Revertendo.");
            setSystemSettings({ ...systemSettings, allowTrialRegistration: !newValue }); // revert on error
        } finally {
            setSettingsUpdating(false);
        }
    };

    // 2. CRIAR EMPRESA
    const handleCreateCompany = async () => {
        setIsProcessing(true);
        try {
            await api.post('/register', {
                ...newCompany,
                companyName: newCompany.name,
                usersLimit: newCompany.maxUsers
            }); // Reusa rota de registro ou cria /admin/companies
            alert("Empresa criada com sucesso!");
            setIsCreateModalOpen(false);
            setNewCompany({ name: '', adminName: '', adminEmail: '', adminPassword: '', cpf_cnpj: '', maxUsers: 3, modules: { financeiro: false, notas_fiscais: false, integracoes: false }, next_due_date: '' });
            fetchData();
        } catch (error) {
            alert("Erro ao criar empresa: " + (error.response?.data?.error || error.message));
        } finally {
            setIsProcessing(false);
        }
    };

    // 3. EDITAR EMPRESA (Alterar plano, status)
    const handleUpdateCompany = async () => {
        if (!editingCompany) return;
        setIsProcessing(true);
        try {
            await api.put(`/admin/companies/${editingCompany.id}`, editingCompany);
            alert("Empresa atualizada!");
            setIsEditModalOpen(false);
            setEditingCompany(null);
            fetchData();
        } catch (error) {
            alert("Erro ao atualizar.");
        } finally {
            setIsProcessing(false);
        }
    };

    // 4. SUSPENDER / EXCLUIR
    const handleToggleStatus = async (company) => {
        const newStatus = company.status === 'active' ? 'suspended' : 'active';
        if (!window.confirm(`Deseja alterar status para ${newStatus}?`)) return;

        try {
            await api.put(`/admin/companies/${company.id}`, { status: newStatus });
            fetchData();
        } catch (e) { alert("Erro ao alterar status"); }
    };

    const handleImpersonate = async (companyId) => {
        setIsProcessing(true);
        try {
            const res = await api.post(`/admin/impersonate/${companyId}`);
            const { token, user } = res.data;
            
            // Salvar sessão do super admin para permitir retorno direto ao sair
            const currentToken = localStorage.getItem('token');
            const currentUser = localStorage.getItem('user');
            if (currentToken) localStorage.setItem('superAdminToken', currentToken);
            if (currentUser) localStorage.setItem('superAdminUser', currentUser);
            
            // Logar silenciosamente como cliente
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            
            window.location.href = '/'; // Vai direto para o painel principal da empresa
        } catch (e) {
            alert("Erro ao acessar conta silenciosamente: " + (e.response?.data?.error || e.message));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteCompany = async (id) => {
        if (!window.confirm("ATENÇÃO: Isso apagará TODOS os dados desta empresa. Continuar?")) return;
        try {
            await api.delete(`/admin/companies/${id}`);
            fetchData();
        } catch (e) { alert("Erro ao excluir."); }
    };

    // 5. ENVIAR NOTIFICAÇÃO
    const handleSendNotice = async () => {
        if (!noticeForm.title || !noticeForm.message) return alert("Título e Mensagem são obrigatórios.");
        setIsProcessing(true);
        try {
            await api.post('/admin/system-notices', noticeForm);
            alert("Notificação enviada com sucesso!");
            setIsNoticeModalOpen(false);
            setNoticeForm({ title: '', message: '', imageUrl: '', targetCompanyId: '' });
        } catch (error) {
            alert("Erro ao enviar notificação.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Filtro local
    const filteredCompanies = companies.filter(c =>
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.owner_email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
            {/* Header */}
            <header className="bg-gray-900 text-white shadow-lg z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center">
                        <Building2 className="w-6 h-6 mr-2 text-blue-400" />
                        <h1 className="text-xl font-bold tracking-wider">SUPER ADMIN (Local)</h1>
                    </div>
                    <button onClick={onLogout} className="flex items-center text-gray-300 hover:text-white px-3 py-2 rounded-md hover:bg-gray-800 transition-colors">
                        <LogOut className="w-5 h-5 mr-2" /> Sair
                    </button>
                </div>
            </header>

            {/* Conteúdo */}
            <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-8">

                {/* Tabs */}
                <div className="flex space-x-6 mb-6 border-b border-gray-200">
                    <button onClick={() => setActiveTab('companies')} className={`pb-3 font-bold text-sm tracking-wide transition-colors ${activeTab === 'companies' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-800'}`}>EMPRESAS</button>
                    <button onClick={() => setActiveTab('notices')} className={`pb-3 font-bold text-sm tracking-wide transition-colors ${activeTab === 'notices' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-800'}`}>AVISOS DO SISTEMA</button>
                    <button onClick={() => setActiveTab('support')} className={`pb-3 font-bold text-sm tracking-wide transition-colors ${activeTab === 'support' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-800'}`}>SUPORTE</button>
                    <button onClick={() => setActiveTab('modules')} className={`pb-3 font-bold text-sm tracking-wide transition-colors ${activeTab === 'modules' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-800'}`}>MÓDULOS & PREÇOS</button>
                </div>

                {activeTab === 'support' ? (
                    <SupportPanel />
                ) : activeTab === 'notices' ? (
                    <SystemNoticesPanel />
                ) : activeTab === 'modules' ? (
                    <ModulesPanel 
                        systemSettings={systemSettings} 
                        companies={companies} 
                        onSaveSettings={async (newSet) => {
                            await api.put('/admin/settings', newSet);
                            setSystemSettings(prev => ({ ...prev, ...newSet }));
                        }}
                    />
                ) : (
                    <>
                        {/* Dashboard Header c/ Configurações Globais */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 cursor-pointer transition-colors ${systemSettings.allowTrialRegistration ? 'bg-green-500' : 'bg-gray-300'} ${settingsUpdating ? 'opacity-50 pointer-events-none' : ''}`} onClick={handleToggleTrial}>
                            <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${systemSettings.allowTrialRegistration ? 'translate-x-6' : ''}`}></div>
                        </div>
                        <div>
                            <p className="font-bold text-gray-800 text-sm">Cadastro: Testar Grátis</p>
                            <p className="text-xs text-gray-500">{systemSettings.allowTrialRegistration ? 'Aberta para novos visitantes (Landing Page)' : 'Fechada (Aparece como "Desativado temporariamente")'}</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Buscar empresa ou email..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center font-medium shadow-sm"
                        >
                            <PlusCircle className="w-5 h-5 mr-2" /> Nova Empresa
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20"><Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600" /></div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-bold border-b">
                                <tr>
                                    <th className="px-6 py-4">Empresa</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Vencimento</th>
                                    <th className="px-6 py-4">WhatsApp</th>
                                    <th className="px-6 py-4">Métricas Básicas</th>
                                    <th className="px-6 py-4 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {filteredCompanies.map(company => {
                                    const isExpired = company.next_due_date && new Date(company.next_due_date) < new Date();
                                    const isConnected = company.connectionStatus === 'open'; // open = conectado no Baileys

                                    const getCreationDate = (c) => {
                                        if (c.createdAt) return new Date(c.createdAt);
                                        const id = c._id || c.id;
                                        if (id && typeof id === 'string' && id.length === 24) {
                                            return new Date(parseInt(id.substring(0, 8), 16) * 1000);
                                        }
                                        return new Date();
                                    };

                                    return (
                                    <tr key={company.id || company._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-bold text-gray-900">{company.name}</p>
                                            <div className="flex items-center text-gray-500 mt-1">
                                                <Mail className="w-3 h-3 mr-1" /> {company.owner_email}
                                            </div>
                                            <div className="flex flex-col gap-0.5 text-gray-400 text-xs mt-1">
                                                <div className="flex items-center">
                                                    <Calendar className="w-3 h-3 mr-1" /> Desde {getCreationDate(company).toLocaleDateString('pt-BR')}
                                                </div>
                                                {company.lastLogin && (
                                                    <div className="flex items-center text-blue-500 font-medium">
                                                        <CheckCircle className="w-3 h-3 mr-1" /> Último login: {new Date(company.lastLogin).toLocaleDateString('pt-BR')} às {new Date(company.lastLogin).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold flex w-fit items-center gap-1
                                                ${company.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {company.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                                                {company.status === 'active' ? 'Ativo' : 'Suspenso'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {company.next_due_date ? (
                                                <div className={`flex items-center text-sm font-medium ${isExpired ? 'text-red-600' : 'text-gray-700'}`}>
                                                    {isExpired && <AlertTriangle className="w-4 h-4 mr-1" />}
                                                    {new Date(company.next_due_date).toLocaleDateString('pt-BR')}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs italic">Não definido</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md w-fit ${
                                                company.connectionStatus === 'CONNECTED' ? 'bg-green-50 text-green-700 border border-green-200' : 
                                                ['STARTING', 'CONNECTING', 'RECONNECTING'].includes(company.connectionStatus) ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                                                company.connectionStatus === 'QR_READY' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                company.connectionStatus === 'CONNECTION_ERROR' ? 'bg-red-50 text-red-700 border border-red-200' :
                                                'bg-gray-100 text-gray-500 border border-gray-200'
                                            }`}>
                                                {company.connectionStatus === 'CONNECTED' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                                {company.connectionStatus === 'CONNECTED' ? 'Conectado' : 
                                                 ['STARTING', 'CONNECTING', 'RECONNECTING'].includes(company.connectionStatus) ? 'Conectando...' :
                                                 company.connectionStatus === 'QR_READY' ? 'Aguardando QR' :
                                                 company.connectionStatus === 'CONNECTION_ERROR' ? 'Erro de Conexão' : 'Desconectado'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1 text-xs text-gray-600">
                                                <div className="flex items-center"><Users className="w-3 h-3 mr-1 text-blue-500" /> {company.usersLimit || company.maxUsers} Limite Users</div>
                                                <div className="flex items-center"><Users className="w-3 h-3 mr-1 text-indigo-500" /> {company.clientCount || 0} Clientes</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center space-x-2">
                                                <button onClick={() => setSelectedDashboardCompany(company)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full" title="Dashboard Completo">
                                                    <BarChart3 className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => { setEditingCompany(company); setIsEditModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full" title="Editar">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleToggleStatus(company)} className={`p-2 rounded-full ${company.status === 'active' ? 'text-orange-500 hover:bg-orange-50' : 'text-green-500 hover:bg-green-50'}`} title="Bloquear/Desbloquear">
                                                    <PlayCircle className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => { setNoticeForm({ ...noticeForm, targetCompanyId: company._id || company.id }); setIsNoticeModalOpen(true); }} className="p-2 text-purple-600 hover:bg-purple-50 rounded-full" title="Enviar Notificação Direta">
                                                    <Send className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleImpersonate(company._id || company.id)} className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-full" title="Acesso Silencioso">
                                                    <Key className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleDeleteCompany(company._id || company.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-full" title="Excluir">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredCompanies.length === 0 && <div className="p-10 text-center text-gray-400">Nenhuma empresa encontrada.</div>}
                    </div>
                )}
                </>
                )}
            </main>

            {/* MODAL CRIAR */}
            <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Nova Empresa" confirmText="Criar" onConfirm={handleCreateCompany} isLoading={isProcessing}>
                <div className="space-y-3">
                    <input type="text" placeholder="Nome da Empresa" className="w-full p-2 border rounded" value={newCompany.name} onChange={e => setNewCompany({ ...newCompany, name: e.target.value })} />
                    <input type="text" placeholder="Nome Admin" className="w-full p-2 border rounded" value={newCompany.adminName} onChange={e => setNewCompany({ ...newCompany, adminName: e.target.value })} />
                    <input type="email" placeholder="Email Admin" className="w-full p-2 border rounded" value={newCompany.adminEmail} onChange={e => setNewCompany({ ...newCompany, adminEmail: e.target.value })} />
                    <input type="password" placeholder="Senha Admin" className="w-full p-2 border rounded" value={newCompany.adminPassword} onChange={e => setNewCompany({ ...newCompany, adminPassword: e.target.value })} />
                    <input type="text" placeholder="CPF/CNPJ" className="w-full p-2 border rounded" value={newCompany.cpf_cnpj} onChange={e => setNewCompany({ ...newCompany, cpf_cnpj: e.target.value })} />
                    <div className="flex items-center gap-2">
                        <span>Limite Usuários:</span>
                        <input type="number" className="p-2 border rounded w-20" value={newCompany.maxUsers} onChange={e => setNewCompany({ ...newCompany, maxUsers: parseInt(e.target.value) })} />
                    </div>
                    <div className="flex flex-col gap-2 p-3 border rounded bg-gray-50">
                        <span className="font-semibold text-sm text-gray-700">Módulos:</span>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={newCompany.modules.financeiro} onChange={e => setNewCompany({ ...newCompany, modules: { ...newCompany.modules, financeiro: e.target.checked } })} />
                            Gestão Financeira
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={newCompany.modules.notas_fiscais} onChange={e => setNewCompany({ ...newCompany, modules: { ...newCompany.modules, notas_fiscais: e.target.checked } })} />
                            Notas Fiscais
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={newCompany.modules.integracoes} onChange={e => setNewCompany({ ...newCompany, modules: { ...newCompany.modules, integracoes: e.target.checked } })} />
                            Integrações
                        </label>
                    </div>
                </div>
            </Modal>

            {/* MODAL EDITAR */}
            {editingCompany && (
                <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar Empresa" confirmText="Salvar" onConfirm={handleUpdateCompany} isLoading={isProcessing}>
                    <div className="space-y-3">
                        <input type="text" placeholder="Nome da Empresa" className="w-full p-2 border rounded" value={editingCompany.name} onChange={e => setEditingCompany({ ...editingCompany, name: e.target.value })} />
                        <div className="flex items-center gap-2">
                            <span>Limite Usuários:</span>
                            <input type="number" className="p-2 border rounded w-20" value={editingCompany.maxUsers} onChange={e => setEditingCompany({ ...editingCompany, maxUsers: parseInt(e.target.value) })} />
                        </div>
                        <div className="flex items-center gap-2">
                            <span>Vencimento:</span>
                            <input type="date" className="p-2 border rounded w-full" value={editingCompany.next_due_date ? new Date(editingCompany.next_due_date).toISOString().split('T')[0] : ''} onChange={e => setEditingCompany({ ...editingCompany, next_due_date: e.target.value })} />
                        </div>
                        <select className="w-full p-2 border rounded" value={editingCompany.status} onChange={e => setEditingCompany({ ...editingCompany, status: e.target.value })}>
                            <option value="active">Ativo</option>
                            <option value="suspended">Suspenso</option>
                            <option value="overdue">Pagamento Pendente</option>
                        </select>
                        <div className="flex flex-col gap-2 p-3 border rounded bg-gray-50 mt-2">
                            <span className="font-semibold text-sm text-gray-700">Módulos:</span>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={editingCompany.modules?.financeiro || false} onChange={e => setEditingCompany({ ...editingCompany, modules: { ...(editingCompany.modules || {}), financeiro: e.target.checked } })} />
                                Gestão Financeira
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={editingCompany.modules?.notas_fiscais || false} onChange={e => setEditingCompany({ ...editingCompany, modules: { ...(editingCompany.modules || {}), notas_fiscais: e.target.checked } })} />
                                Notas Fiscais
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={editingCompany.modules?.integracoes || false} onChange={e => setEditingCompany({ ...editingCompany, modules: { ...(editingCompany.modules || {}), integracoes: e.target.checked } })} />
                                Integrações
                            </label>
                        </div>
                    </div>
                </Modal>
            )}

            {/* MODAL DASHBOARD DA EMPRESA */}
            {selectedDashboardCompany && (
                <Modal isOpen={!!selectedDashboardCompany} onClose={() => setSelectedDashboardCompany(null)} title={`Dashboard: ${selectedDashboardCompany.name}`} confirmText="Fechar" onConfirm={() => setSelectedDashboardCompany(null)}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl text-center">
                                <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-blue-900">{selectedDashboardCompany.clientCount || 0}</p>
                                <p className="text-xs text-blue-600 uppercase font-semibold mt-1">Clientes Salvos</p>
                            </div>
                            <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center">
                                <FileText className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-indigo-900">{selectedDashboardCompany.ticketCount || 0}</p>
                                <p className="text-xs text-indigo-600 uppercase font-semibold mt-1">Atendimentos (Total)</p>
                            </div>
                            <div className="bg-purple-50 border border-purple-100 p-4 rounded-xl text-center">
                                <Building2 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-purple-900">{selectedDashboardCompany.userCount || 0}</p>
                                <p className="text-xs text-purple-600 uppercase font-semibold mt-1">Usuários/Atendentes</p>
                            </div>
                            <div className={`border p-4 rounded-xl text-center ${selectedDashboardCompany.connectionStatus === 'open' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'}`}>
                                {selectedDashboardCompany.connectionStatus === 'open' ? <Wifi className="w-6 h-6 text-green-600 mx-auto mb-2" /> : <WifiOff className="w-6 h-6 text-gray-400 mx-auto mb-2" />}
                                <p className={`text-lg font-bold ${selectedDashboardCompany.connectionStatus === 'open' ? 'text-green-900' : 'text-gray-700'}`}>
                                    {selectedDashboardCompany.connectionStatus === 'open' ? 'Conectado' : 'Desconectado'}
                                </p>
                                <p className={`text-xs uppercase font-semibold mt-1 ${selectedDashboardCompany.connectionStatus === 'open' ? 'text-green-600' : 'text-gray-500'}`}>Status do WhatsApp</p>
                            </div>
                        </div>
                        
                        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
                            <h4 className="font-bold text-gray-800 mb-2 flex items-center"><Calendar className="w-4 h-4 mr-2" /> Detalhes da Assinatura</h4>
                            <div className="grid grid-cols-2 gap-y-2 text-sm">
                                <div className="text-gray-500">Plano Atual:</div>
                                <div className="font-semibold uppercase">{selectedDashboardCompany.plan || 'PRO'}</div>
                                <div className="text-gray-500">Vencimento:</div>
                                <div className="font-semibold">
                                    {selectedDashboardCompany.next_due_date ? new Date(selectedDashboardCompany.next_due_date).toLocaleDateString('pt-BR') : 'Não definido'}
                                </div>
                                <div className="text-gray-500">Módulos:</div>
                                <div className="font-semibold">
                                    {selectedDashboardCompany.modules?.financeiro && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs mr-1">Financeiro</span>}
                                    {selectedDashboardCompany.modules?.notas_fiscais && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">NFS-e</span>}
                                    {!selectedDashboardCompany.modules?.financeiro && !selectedDashboardCompany.modules?.notas_fiscais && <span className="text-gray-400">Padrão</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* MODAL NOTIFICAÇÃO DO SISTEMA */}
            <Modal isOpen={isNoticeModalOpen} onClose={() => setIsNoticeModalOpen(false)} title="Aviso do Sistema" confirmText="Enviar Notificação" onConfirm={handleSendNotice} isLoading={isProcessing}>
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-start gap-3">
                        <Send className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                            <p className="font-semibold mb-1">Como funciona?</p>
                            <p>O aviso aparecerá para os usuários quando eles logarem ou acessarem a plataforma. Você pode direcionar para uma empresa específica ou para <strong>todos os clientes</strong> do sistema.</p>
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Empresa Alvo</label>
                        <select className="w-full p-2 border rounded-lg" value={noticeForm.targetCompanyId} onChange={e => setNoticeForm({ ...noticeForm, targetCompanyId: e.target.value })}>
                            <option value="">Todas as Empresas (Aviso Global)</option>
                            {companies.map(c => (
                                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Título</label>
                        <input type="text" placeholder="Ex: Manutenção Programada, Nova Funcionalidade..." className="w-full p-2 border rounded-lg" value={noticeForm.title} onChange={e => setNoticeForm({ ...noticeForm, title: e.target.value })} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Mensagem</label>
                        <textarea rows={4} placeholder="Escreva o aviso detalhado aqui..." className="w-full p-2 border rounded-lg resize-none" value={noticeForm.message} onChange={e => setNoticeForm({ ...noticeForm, message: e.target.value })} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">URL da Imagem (Opcional)</label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input type="text" placeholder="https://..." className="w-full pl-9 p-2 border rounded-lg" value={noticeForm.imageUrl} onChange={e => setNoticeForm({ ...noticeForm, imageUrl: e.target.value })} />
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Cole o link de uma imagem hospedada para ilustrar o aviso.</p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SuperAdminScreen;